<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import type { FormInstance, FormRules } from "element-plus";
import { ElMessage } from "element-plus";
import UiDialogField from "@shared/components/UiDialogFields/UiDialogField.vue";
import UiDialogFields from "@shared/components/UiDialogFields/index.vue";
import { t } from "@shared/utils/i18n";
import { isApiError } from "@shared/utils/request";

defineOptions({ name: "SystemChangePasswordDialog" });

interface PasswordFormModel {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}

const props = withDefaults(
    defineProps<{
        /** 强制改密：不可关闭、无取消按钮；仍需填写原密码 */
        force?: boolean;
        /** 由各端注入；勿在弹窗内写死 customer/manage 路径 */
        submitApi: (oldPassword: string, newPassword: string) => Promise<void>;
    }>(),
    {
        force: false,
    },
);

const emit = defineEmits<{
    (e: "success"): void;
}>();

const visible = ref(false);
const submitting = ref(false);
const formRef = ref<FormInstance>();

const form = reactive<PasswordFormModel>({ oldPassword: "", newPassword: "", confirmPassword: "" });

function validateConfirm(_rule: unknown, value: string, callback: (err?: Error) => void) {
    if (!value) return callback(new Error(t("请再次输入新密码")));
    if (value !== form.newPassword) return callback(new Error(t("两次输入的密码不一致")));
    callback();
}

const rules = computed<FormRules>(() => ({
    oldPassword: [{ required: true, message: t("请输入原密码") }],
    newPassword: [
        { required: true, message: t("请输入新密码") },
        { min: 6, max: 20, message: t("密码长度为6~20位") },
    ],
    confirmPassword: [{ required: true, validator: validateConfirm, trigger: "blur" }],
}));

function open() {
    form.oldPassword = "";
    form.newPassword = "";
    form.confirmPassword = "";
    formRef.value?.clearValidate();
    visible.value = true;
}

function close() {
    if (props.force) return;
    visible.value = false;
}

async function handleSubmit() {
    if (!formRef.value) return;
    const valid = await formRef.value.validate().catch(() => false);
    if (!valid) return;

    submitting.value = true;
    try {
        await props.submitApi(form.oldPassword, form.newPassword);
        // 成功提示由父级统一处理（个人中心 / 强制改密均为「请使用新密码登录」）
        emit("success");
        if (!props.force) {
            visible.value = false;
        }
    } catch (e) {
        if (!isApiError(e)) {
            ElMessage.error(e instanceof Error ? e.message : t("密码修改失败"));
        }
    } finally {
        submitting.value = false;
    }
}

defineExpose({ open, close });
</script>

<template>
    <el-dialog
        v-model="visible"
        :title="force ? t('首次登录请修改密码') : t('修改密码')"
        :width="400"
        destroy-on-close
        class="system-change-password-dialog ui-dialog--xs"
        :show-close="!force"
        :close-on-click-modal="!force"
        :close-on-press-escape="!force"
        @close="close"
    >
        <el-form ref="formRef" :model="form" :rules="rules" :validate-on-rule-change="false" label-position="left" label-width="0" class="system-change-password-dialog__form">
            <UiDialogFields variant="form" :cols="1">
                <UiDialogField :label="t('原密码')" required>
                    <el-form-item prop="oldPassword">
                        <el-input v-model="form.oldPassword" data-testid="force-password-old" type="password" :placeholder="t('请输入原密码')" show-password clearable />
                    </el-form-item>
                </UiDialogField>
                <UiDialogField :label="t('新密码')" required>
                    <el-form-item prop="newPassword">
                        <el-input v-model="form.newPassword" data-testid="force-password-new" type="password" :placeholder="t('请输入新密码')" show-password clearable />
                    </el-form-item>
                </UiDialogField>
                <UiDialogField :label="t('确认密码')" required>
                    <el-form-item prop="confirmPassword">
                        <el-input v-model="form.confirmPassword" data-testid="force-password-confirm" type="password" :placeholder="t('请再次输入新密码')" show-password clearable />
                    </el-form-item>
                </UiDialogField>
            </UiDialogFields>
        </el-form>

        <template #footer>
            <el-button v-if="!force" @click="close">{{ t("取消") }}</el-button>
            <el-button data-testid="force-password-submit" type="primary" :loading="submitting" @click="handleSubmit">
                {{ t("确定") }}
            </el-button>
        </template>
    </el-dialog>
</template>

<style scoped lang="scss">
.system-change-password-dialog__form {
    :deep(.ui-dialog-field .el-form-item) {
        margin-bottom: 0;
        width: 100%;
    }

    :deep(.ui-dialog-field .el-form-item__content) {
        margin-left: 0 !important;
        flex: 1;
        min-width: 0;
    }

    :deep(.ui-dialog-field .el-form-item__label) {
        display: none;
    }
}
</style>
